// CTSK-509 — may this user see and use Notes right now?
//
// All of these must hold:
//   1. /meta/features has answered (`resolved`). Until then the flags are the
//      Vite fallback; asking the API anything notes-related before that is the
//      CTSK-504 race (hotfix dc76513) — a 404 request fires for flag-off orgs.
//   2. the per-org Settings → Documents "enableNotes" toggle is not off. An
//      ABSENT key means on (the setting has always defaulted to true), exactly
//      like the API gate in lib/notes-gate.ts.
//   3. the session holds notes:view.
// Any failure (no settings:view, offline) resolves to "off": the feature stays
// hidden rather than half-working. The API is the real gate; this is UX.

import { useQuery } from '@tanstack/react-query';
import { apiSingle } from '../lib/api.js';
import { useFeatures } from '../lib/features.js';

interface DocumentsSettingsEnvelope {
  body?: { enableNotes?: unknown } | null;
}

interface SessionEnvelope {
  session: { user: { id: string }; capabilities: string[] };
}

export interface NotesAccess {
  /** Gate + capability resolved and on: render Notes UI and fire notes requests. */
  enabled: boolean;
  /** Both queries have answered; false while the answer is still unknown. */
  ready: boolean;
  userId: string | null;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
}

export function useNotesAccess(): NotesAccess {
  const { resolved } = useFeatures();
  const settingsQ = useQuery({
    queryKey: ['settings', 'documents'],
    queryFn: () => apiSingle<DocumentsSettingsEnvelope>('/settings/documents'),
    enabled: resolved,
    staleTime: 30_000,
    retry: 0,
  });
  const sessionQ = useQuery({
    queryKey: ['auth', 'session', 'notes-access'],
    queryFn: () => apiSingle<SessionEnvelope>('/auth/session'),
    enabled: resolved,
    staleTime: 30_000,
    retry: 0,
  });

  const ready = resolved && !settingsQ.isPending && !sessionQ.isPending;
  const caps = new Set(sessionQ.data?.session.capabilities ?? []);
  const settingsOn = settingsQ.isSuccess && settingsQ.data?.body?.enableNotes !== false;
  const enabled = ready && settingsOn && caps.has('notes:view');
  return {
    enabled,
    ready,
    userId: sessionQ.data?.session.user.id ?? null,
    canCreate: enabled && caps.has('notes:create'),
    canEdit: enabled && caps.has('notes:edit'),
    canDelete: enabled && caps.has('notes:delete'),
  };
}
